import React from "react";
import { PaymentMethod } from "../../../../common/enums";
import { WorkflowMode } from "../../common/enums";
import { type RedactedPaymentPref, type Position, Arena } from "../../common/types";
import { type UpdatePositionsWithPaymentPrefPositionList } from "../../Components/PositionList/UpdatePositionsWithPaymentPrefPositionList";
import { type PositionSingleDisplay } from "../../Components/PositionSingleDisplay";
import { type ChildComponentProps } from "../../Components/types";
import { ConsolidatedPaymentPrefProvider } from "../../ConsolidatedPaymentPrefContextProvider";
import { type AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { BasePaymentPrefWorkflow } from "../BaseWorkflow";

interface PaymentPrefEditForAccountAndPositionWorkflowProps {
  /**
   * Relevant position in context of this workflow.
   */
  position: Position;

  /**
   * The existing payment preference to be edited.
   */
  existingPaymentPref: RedactedPaymentPref;

  /**
   * The initial mutation (through selection of a enum) to associate payment preferences with target objects.
   * The association mutation can also be set later in the workflow, dynamically.
   *
   * Association selection:
   * - `WorkflowType.Account` + `WorkflowMode.Edit` this should be `AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref`
   * - `WorkflowType.Position` + `WorkflowMode.Edit` this should be `AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition`
   */
  initialAssociatePaymentPref:
    | AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref
    | AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition;

  ListOfPositionsToUpdateComponent:
    | typeof UpdatePositionsWithPaymentPrefPositionList
    | typeof PositionSingleDisplay;

  suffixComponents?: React.FC<ChildComponentProps>[];

  onSuccess: () => void;
  onExit: () => void;

  /**
   * Required only on GP flows and
   * has_position_payment_pref_staff_approvals arena FF is enabled
   */
  accountName?: string;

  /**
   * The arena in context of this workflow.
   */
  arena?: Arena;
}

export const PaymentPrefEditForAccountAndPositionWorkflow: React.FC<
  PaymentPrefEditForAccountAndPositionWorkflowProps
> = ({
  position,
  existingPaymentPref,
  initialAssociatePaymentPref,
  onSuccess,
  onExit,
  ListOfPositionsToUpdateComponent,
  suffixComponents,
  accountName,
  arena,
}) => {
  return (
    <ConsolidatedPaymentPrefProvider
      currentPosition={position}
      initialAssociatePaymentPref={initialAssociatePaymentPref}
      initialSelectedPaymentMethod={existingPaymentPref.paymentMethod as PaymentMethod}
      initialPaymentPrefId={existingPaymentPref?.id}
      sourcePaymentPrefId={existingPaymentPref?.id}
      ListOfPositionsToUpdateComponent={ListOfPositionsToUpdateComponent}
      workflowMode={WorkflowMode.Edit}
      accountName={accountName}
      arena={arena}
    >
      <BasePaymentPrefWorkflow
        suffixComponents={suffixComponents}
        onSuccess={onSuccess}
        onExit={onExit}
        workflowMode={WorkflowMode.Edit}
      />
    </ConsolidatedPaymentPrefProvider>
  );
};
